// src/utils/analytics.js
// Frontend Analytics Utilities — Meta Pixel + Google Tag Manager Integration
//
// PURPOSE:
//   Centralizes all dataLayer push logic for consistency across all form components.
//   The event_id generated here is the SAME one sent to the backend, enabling
//   Meta Pixel ↔ CAPI deduplication via GTM's Lead event trigger.
//
// DEDUPLICATION FLOW:
//   generateEventId() ──→ sent to backend (req.body.event_id)
//                    └──→ pushed to dataLayer (event_id field)
//   Meta Pixel fires from GTM with this event_id  ┐
//   Meta CAPI fires from backend with same event_id ┘ → Meta deduplicates → counts once

/**
 * Generates a universally unique event ID for Meta Pixel/CAPI deduplication.
 * Uses crypto.randomUUID() — available in all modern browsers and Node.js 14+.
 * Falls back to a timestamp-based ID for older environments.
 *
 * @returns {string} A unique event identifier string
 */
export const generateEventId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `lead_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
};

/**
 * Pushes a lead_form_submitted event to window.dataLayer for GTM.
 * GTM then triggers the Meta Pixel Lead event with this data.
 *
 * @param {string} formType - Identifier for the form (e.g. 'quick_booking_bar')
 * @param {string} eventId  - The unique event ID (must match what was sent to backend)
 * @param {Object} userData - User information object
 * @param {string} [userData.email]   - User's email address (optional)
 * @param {string} [userData.phone]   - User's phone number (optional)
 * @param {string} [userData.name]    - User's first/full name (optional)
 */
export const trackLeadSubmission = (formType, eventId, userData = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event:      'lead_form_submitted',
    event_id:   eventId,
    form_type:  formType,
    user_data: {
      email:        userData.email       || '',
      phone_number: userData.phone       || '',
      first_name:   userData.name        || '',
    },
  });
};
